import { SIDEBAR_WIDTH, appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { useAccessToken } from "@/hooks";
import {
  getTwitchUser,
  twitchLogin,
  useGetRemainingEarnableGems,
  useGetTwitchDrops,
} from "@/hooks/quest/twitchDropHooks";
import { useTopHeroScrollMotion } from "@/hooks/useTopHeroScrollMotion";
import { notifyError } from "@/utils/monitoring/newrelic";
import { authorizedPost } from "@/utils/request";
import { trackLegacyEvent } from "@analytics";
import {
  AnimateHeightBox,
  Body,
  Box,
  BulletList,
  Button,
  Chip,
  ConfirmationDialog,
  CountdownTimer,
  Divider,
  Grid,
  Heading,
  HeroBackground,
  HeroCard,
  Icon,
  Link,
  Stack,
  centerFlexChildren,
  hFlex,
  mediaQuerySizesHelper,
  useIntersectionObserver,
  usePrevious,
  useTheme,
} from "@biom3/react";
import { getWebLink } from "@cms/utils";
import type { Content } from "@prismicio/client";
import type { GameSpecifications } from "@ui-kit/GameSpecs";
import { mapPlatformIcons } from "@ui-kit/GameSpecs/Platforms";
import { PremiumGameHeader } from "@ui-kit/PremiumGameHeader";
import { useIsLessThanMediumScreen } from "@ui-kit/hooks/screenSizeHooks";
import { AxiosError } from "axios";
import dynamic from "next/dynamic";
import Image from "next/image";
import NextLink from "next/link";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { merge } from "ts-deepmerge";

const TWITCH_PURPLE = "#a855f7";

const TREEVERSE_TWITCH_DROPS = [
  {
    id: "38b29ce1-0f4b-11f0-957a-0a58a9feac02",
    name: "Starter Pack",
    description:
      "Use this pack to unlock a full Horn Owl set in game including its weapons.",
    image:
      "https://play-assets.immutable.com/images/treeverse-twitch-drops/StarterPack.webp",
  },
  {
    id: "150e564f-0f4b-11f0-aea5-0a58a9feac02",
    name: "Advanced Pack",
    description:
      "Use this pack to unlock a full Arcane Set in game including its weapons.",
    image:
      "https://play-assets.immutable.com/images/treeverse-twitch-drops/AdvancedPack.webp",
  },
  {
    id: "2b93945a-0f4b-11f0-8df0-0a58a9feac02",
    name: "Experience Talisman 2hr",
    description: "Use this to get a buff, granting 2x experience for 2hrs",
    image:
      "https://play-assets.immutable.com/images/treeverse-twitch-drops/ExperienceTalisman2hr.webp",
  },
];

const TREEVERSE_CLIENT_ID = "d3f8zzq23lirmyah68itjsynp69vrk";

const TREEVERSE_TWITCH_STREAMERS = [
  "bale_gg",
  "revolaimar",
  "pompeyo4",
  "srtumbao",
  "adrianpiedra",
  "drekzenn",
  "murloka",
  "cellofrag",
  "ninuschk",
  "t666ommy",
  "whithblade",
  "sadicaz",
  "elpelotasssss",
  "Feeckz",
  "Luuu",
  "xButko",
  "xbunnyxmeowx",
  "yosoyricklive",
];

function DropsDivider({
  children,
  sx = {},
}: { children: ReactNode; sx?: ComponentProps<typeof Divider>["sx"] }) {
  return (
    <Divider
      size="xSmall"
      sx={merge(
        {
          mt: { default: "base.spacing.x10", large: "base.spacing.x12" },
          mb: "base.spacing.x5",
        },
        sx,
      )}
    >
      {children}
    </Divider>
  );
}

function GameLinks({
  platforms,
  links,
  game,
}: {
  platforms: string[];
  links: string[];
  game: Content.GameDocument;
}) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="flex-start"
      justifyContent="flex-start"
      gap="base.spacing.x4"
    >
      {platforms.map((platform, idx) => {
        const icon = mapPlatformIcons(platform);
        const link = links?.[idx];

        if (!icon) return null;

        return (
          <Button
            variant="primary"
            onClick={() => {
              trackLegacyEvent({
                userJourney: "Games",
                screen: "TwitchDrops",
                control: "Platform",
                controlType: "Button",
                action: "Pressed",
                extras: { platform, game: game.uid },
              });
            }}
            key={`${icon}-${link}`}
            rc={
              link ? (
                <NextLink
                  onClick={() => {}}
                  href={link}
                  target="_blank"
                  referrerPolicy="no-referrer"
                />
              ) : undefined
            }
          >
            <Button.Icon icon={icon} />
            {platform}
          </Button>
        );
      })}
    </Stack>
  );
}

function TwitchFlavouredBiomeButton({
  onClick,
  children,
  size,
  disabled,
  sx = {},
}: {
  onClick?: () => void;
  children: ReactNode;
  size: ComponentProps<typeof Button>["size"];
  disabled?: boolean;
  sx?: ComponentProps<typeof Button>["sx"];
}) {
  return (
    <Button
      onClick={onClick}
      size={size}
      variant="primary"
      disabled={disabled}
      sx={merge(
        {
          bg: TWITCH_PURPLE,
          "&::before": { bg: TWITCH_PURPLE },
          "&.Button--medium:hover:not([disabled])": {
            boxShadow: `inset 0 0 0 3px ${TWITCH_PURPLE}`,
          },
          "&.Button--medium:active:not([disabled])": {
            boxShadow: `inset 0 0 0 3px ${TWITCH_PURPLE}`,
          },
          "&.Button--large:hover:not([disabled])": {
            boxShadow: `inset 0 0 0 4px ${TWITCH_PURPLE}`,
          },
          "&.Button--large:active:not([disabled])": {
            boxShadow: `inset 0 0 0 4px ${TWITCH_PURPLE}`,
          },
        },
        sx,
      )}
    >
      {children}
    </Button>
  );
}

type GameDropsLayoutProps = {
  defaultBgImage: string | null | undefined;
  game: Content.GameDocument;
  page: Content.PremiumGameDetailsPageDocument;
  gameSpecs: GameSpecifications;
};

export const TREEVERSE_TWITCH_DROPS_DATE = new Date("2025-04-26T12:00:00.000Z");
export const TREEVERSE_TWITCH_DROPS_END_DATE = new Date(
  "2025-05-12T11:59:00.000Z",
);

function UnwrappedGameDropsLayout({
  defaultBgImage,
  game,
  page,
  gameSpecs,
}: GameDropsLayoutProps) {
  const { base } = useTheme();
  const {
    isLoggedIn,
    walletAddress,
    isLoggingIn: passportLoginLoading,
  } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const playLink = getWebLink(game.data.website).url;
  const pageTitle = game.data.name ?? "";
  const heroImageRef = useRef<HTMLImageElement | null>(null);
  const { isIntersecting, domRef: setIORef } = useIntersectionObserver();
  // Authorization code for OAuth flow
  const [authorizationCode, setAuthorizationCode] = useState<string | null>(
    null,
  );
  const previousAuthorizationCode = usePrevious(authorizationCode);
  const [twitchUsername, setTwitchUsername] = useState<string | undefined>(
    undefined,
  );
  const [loggingIntoTwitch, setLoggingIntoTwitch] = useState(false);
  const getAccessToken = useAccessToken();
  const gameUUID = (game?.data?.games_service_item?.id as string) || "";
  const isLoggedInToTwitch = useMemo(
    () => isLoggedIn === true && !!twitchUsername,
    [isLoggedIn, twitchUsername],
  );
  const campaignNotStarted = useMemo(() => {
    return new Date() < TREEVERSE_TWITCH_DROPS_DATE;
  }, []);

  useTopHeroScrollMotion({
    heroImageRef,
    isIntersecting,
  });
  const isSmallScreenMode = useIsLessThanMediumScreen();
  const onLoginClick = useCallback(() => {
    trackLegacyEvent({
      userJourney: "Games",
      screen: "TwitchDrops",
      control: "SignIn",
      controlType: "Button",
      action: "Pressed",
      extras: { path: window.location.pathname, game: game.uid },
    });
    openLoginModal();
  }, [openLoginModal, game.uid]);

  const { data: twitchDropsResponse, isLoading: isLoadingTwitchDrops } =
    useGetTwitchDrops(
      gameUUID,
      TREEVERSE_CLIENT_ID,
      isLoggedInToTwitch,
      game.uid,
    );
  const dropStatus = useMemo(() => {
    return (
      twitchDropsResponse?.data?.reduce(
        (acc, drop) => {
          acc[drop.benefit_id] = drop.fulfillment_status;
          return acc;
        },
        {} as Record<string, string>,
      ) ?? {}
    );
  }, [twitchDropsResponse]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (!code) {
      return;
    }

    setAuthorizationCode(code);
  }, []);

  useEffect(() => {
    const fetchTwitchUsername = async () => {
      const user = await getTwitchUser(gameUUID);
      setTwitchUsername(user?.username);
    };
    fetchTwitchUsername();
  }, [gameUUID]);

  const handleTwitchLogin = useCallback(
    async (code: string) => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }
      setLoggingIntoTwitch(true);
      try {
        setTwitchUsername(
          await twitchLogin(accessToken, code, gameUUID, game.uid),
        );
      } catch (error) {
        notifyError(error, "twitchLogin");
      }

      // On successful connection, add the game to the user's wishlist
      try {
        await authorizedPost(
          "/v1/games/wishlist",
          { game_id: gameUUID },
          accessToken,
        );
      } catch (error) {
        // Ignore 400 errors as they are expected (already)
        if (error instanceof AxiosError && error.response?.status !== 400) {
          notifyError(error, "wishlist");
        }
      }
      setLoggingIntoTwitch(false);
    },
    [getAccessToken, gameUUID, game.uid],
  );

  useEffect(() => {
    if (authorizationCode && authorizationCode !== previousAuthorizationCode) {
      handleTwitchLogin(authorizationCode);
    }
  }, [authorizationCode, previousAuthorizationCode, handleTwitchLogin]);

  const onTwitchConnectClick = useCallback(async () => {
    trackLegacyEvent({
      userJourney: "Games",
      screen: "TwitchDrops",
      control: "ConnectTwitch",
      controlType: "Button",
      action: "Pressed",
      extras: { game: game.uid },
    });
    // Refactor to get the clientId based on the gameId
    const redirectUrl = `${appConfig.BASE_REDIRECT_URL}/twitch-drops/${game.uid}`;
    window.open(
      `https://id.twitch.tv/oauth2/authorize?response_type=code&client_id=${TREEVERSE_CLIENT_ID}&redirect_uri=${redirectUrl}&scope=openid`,
      "_self",
    );
  }, [game.uid]);

  const [isHowToDialogOpen, setIsHowToDialogOpen] = useState(false);
  const onCloseHowToDialog = useCallback(() => setIsHowToDialogOpen(false), []);
  const remainingEarnableGems = useGetRemainingEarnableGems(game.uid);

  return (
    <>
      {defaultBgImage && (
        <HeroBackground
          sx={{
            pos: "absolute",
            top: "0",
            left: "0",
            bradtl: "base.borderRadius.x8",
            bradtr: "base.borderRadius.x8",
          }}
          aspectRatio={{ default: "1", xLarge: "16/11" }}
        >
          <HeroBackground.Image
            loading="eager"
            use={
              <Image
                src={defaultBgImage}
                alt={defaultBgImage}
                fill
                priority
                sizes={mediaQuerySizesHelper([
                  { rule: "default", width: "100dvw - 16px" },
                  {
                    rule: "minWidth",
                    breakpoint: "medium",
                    width: `calc(100dvw - ${SIDEBAR_WIDTH} - 32px)`,
                  },
                ])({ base })}
              />
            }
          />
        </HeroBackground>
      )}
      <Stack
        sx={{ pos: "relative" }}
        testId="GameDetailPage__mainContainer"
        gap={{ default: "base.spacing.x2", medium: "base.spacing.x8" }}
      >
        <PremiumGameHeader
          playLink={playLink}
          game={game}
          stickyPageTitle={pageTitle}
          useMobileExperiment={isSmallScreenMode}
        />

        <Stack
          sx={{ px: { default: "none", large: "base.spacing.x12" } }}
          testId="heroSection"
          direction={{ default: "column", xLarge: "row" }}
          alignItems="flex-end"
          gap={{ default: "base.spacing.x8", large: "base.spacing.x12" }}
        >
          <Box
            sx={{ w: { default: "100%", xLarge: "28%" } }}
            testId="heroSection__rightSide"
          >
            {page.data.overlay_image.url ? (
              <Box
                sx={{
                  // @TODO: for small screens, displaying these characters is alot harder (they have a different crop)
                  d: { default: "none", large: "block" },
                  w: {
                    default: "100%",
                    large: "36dvw",
                    xLarge: "33dvw",
                    xxLarge: "30dvw",
                    xxxLarge: "26dvw",
                  },
                  pos: { default: "static", large: "absolute" },
                  right: {
                    default: "unset",
                    large: `calc(${base.spacing.x10} * -1)`,
                  },
                  top: { default: "unset", large: "base.spacing.x22" },
                  objectFit: "contain",
                  objectPosition: "right center",
                  pointerEvents: "none",
                  userSelect: "none",
                  // @NOTE: all images used here MUST be 8/16 aspect ratio
                  aspectRatio: "8/13",
                }}
              >
                <Image
                  src={page.data.overlay_image.url}
                  loading="eager"
                  alt={page.data.overlay_image.alt ?? ""}
                  priority
                  fill
                  ref={(el) => {
                    if (!el) return;
                    setIORef(el);
                    heroImageRef.current = el;
                  }}
                  data-testid="heroOverlayImage"
                  sizes={mediaQuerySizesHelper([
                    { rule: "default", width: "90vw" },
                    {
                      rule: "minWidth",
                      breakpoint: "large",
                      width: "37vw",
                    },
                    {
                      rule: "minWidth",
                      breakpoint: "xxLarge",
                      width: "32vw",
                    },
                  ])({ base })}
                />
              </Box>
            ) : null}
          </Box>
        </Stack>

        <Box
          testId="contentSection"
          sx={{
            px: { default: "none", large: "base.spacing.x12" },
            w: { default: "100%", large: "70%" },
            // @NOTE: this prevents flickering effect when the header gets stuck inbetween
            // stuck and unstuck (only happens on a very specific sized screen)
            minh: { default: "unset", medium: "90dvh" },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Heading
              size={["medium", null, null, "large"]}
              sx={{ whiteSpace: "nowrap" }}
            >
              Twitch Drops
            </Heading>
            <Button
              size="small"
              variant="tertiary"
              onClick={() => setIsHowToDialogOpen(true)}
            >
              <Button.Icon icon="AirDrop" />
              Participation Info
            </Button>
          </Stack>
          <Body rc={<p />} sx={{ mt: "base.spacing.x5" }} size="large">
            A new way to earn rewards simply by watching{" "}
            <Link
              rc={
                <a
                  href={
                    "http://www.twitch.tv/directory/category/treeverse?filter=drops"
                  }
                  target="_blank"
                  rel="noreferrer"
                />
              }
            >
              Twitch
            </Link>{" "}
            streams of {game.data.name}.
          </Body>
          <Body rc={<p />} sx={{ mt: "base.spacing.x4" }}>
            To get started, connect your Twitch account to your Immutable Play
            account.
          </Body>
          {!!campaignNotStarted && (
            <Stack
              direction="column"
              gap="base.spacing.x2"
              sx={{ mt: "base.spacing.x4" }}
            >
              <Body>The drops campaign will start in:</Body>
              <CountdownTimer
                futureTarget={TREEVERSE_TWITCH_DROPS_DATE}
                size="medium"
                variant="inverse"
                justifyContent={"flex-start"}
              />
            </Stack>
          )}

          <DropsDivider
            sx={{
              ...(isLoggedInToTwitch
                ? { c: "base.color.status.success.bright" }
                : {}),
              ...hFlex,
              ...centerFlexChildren,
              gap: "base.spacing.x2",
            }}
          >
            {isLoggedInToTwitch ? (
              <Icon
                icon="TickWithCircle"
                variant="bold"
                sx={{
                  fill: "base.color.status.success.bright",
                  w: "base.icon.size.200",
                }}
              />
            ) : null}
            Account connection
          </DropsDivider>
          <AnimateHeightBox
            innerContainerSx={{
              ...hFlex,
              flexWrap: "wrap",
              gap: "base.spacing.x4",
              w: "100%",
            }}
          >
            {isLoggedInToTwitch ? (
              <>
                <Chip
                  label={
                    <>
                      Twitch: <strong>{twitchUsername}</strong>
                    </>
                  }
                >
                  <Chip.Icon
                    icon="Twitch"
                    sx={{
                      fill: TWITCH_PURPLE,
                      w: "base.icon.size.200",
                    }}
                  />
                </Chip>
              </>
            ) : (
              <Stack direction="column" gap="base.spacing.x4">
                {!isLoggedIn && (
                  <Body
                    sx={{
                      fontSize: "base.text.body.small.regular.fontSize",
                      color: "base.color.text.body.secondary",
                    }}
                  >
                    Login to Passport first and then connect your Twitch
                    account.
                  </Body>
                )}
                <Stack direction="row" gap="base.spacing.x4">
                  {!isLoggedIn && (
                    <Button
                      onClick={onLoginClick}
                      size={["medium", null, null, null, "large"]}
                    >
                      {passportLoginLoading ? (
                        <>
                          <Button.Icon icon="Loading" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <Button.Logo logo="PassportSymbol" />
                          Sign in with Passport
                        </>
                      )}
                    </Button>
                  )}
                  <TwitchFlavouredBiomeButton
                    onClick={onTwitchConnectClick}
                    size={["medium", null, null, null, "large"]}
                    // Wallet Address is required for the Twitch linking
                    disabled={!walletAddress || !isLoggedIn}
                    sx={{ w: "fit-content" }}
                  >
                    {loggingIntoTwitch ? (
                      <>Connecting...</>
                    ) : isLoggedIn && !walletAddress ? (
                      <>
                        <Button.Icon icon="Loading" />
                        Creating your wallet...
                      </>
                    ) : (
                      <>
                        <Button.Icon icon="Twitch" />
                        Connect Twitch
                      </>
                    )}
                  </TwitchFlavouredBiomeButton>
                </Stack>
                {isLoggedIn && (
                  <Body
                    sx={{
                      fontSize: "base.text.body.small.regular.fontSize",
                      color: "base.color.text.body.secondary",
                    }}
                  >
                    Connecting Twitch will also follow {game.data.name} in
                    Immutable Play
                  </Body>
                )}
              </Stack>
            )}
          </AnimateHeightBox>

          <DropsDivider>Available twitch drops</DropsDivider>

          <Grid minColumnWidth="218px" sx={{ mb: "base.spacing.x4" }}>
            {TREEVERSE_TWITCH_DROPS.map((drop, index) => {
              const status = dropStatus[drop.id];
              return (
                <HeroCard
                  key={drop.id}
                  size="small"
                  aspectRatio="1/1"
                  sx={{
                    position: "relative",
                    "&::after": !status
                      ? {
                          content: '""',
                          position: "absolute",
                          top: "0",
                          left: "0",
                          right: "0",
                          bottom: "0",
                          background: "rgba(0, 0, 0, 0.4)",
                          zIndex: 1,
                        }
                      : undefined,
                  }}
                >
                  <HeroCard.AssetImage
                    imageUrl={drop.image ?? ""}
                    alt={drop.name}
                    relativeImageSizeInLayout="240px"
                  />
                  <HeroCard.Title size="small">{drop.name}</HeroCard.Title>
                  {status && (
                    <HeroCard.Chip
                      label={status === "CLAIMED" ? "Claimed" : "Fulfilled"}
                    >
                      <HeroCard.Chip.Icon
                        icon={
                          status === "CLAIMED" ? "Trophy" : "TickWithCircle"
                        }
                        variant="bold"
                      />
                    </HeroCard.Chip>
                  )}
                </HeroCard>
              );
            })}
          </Grid>
          <Stack direction="column" gap="base.spacing.x4">
            {(!isLoggedIn || !twitchUsername) && (
              <Body size="small">
                Please sign in with Passport &amp; connect your Twitch account
                to see your earned drops.
              </Body>
            )}
            <Body size="small">
              Claimed drops will be distributed every few days.
            </Body>
          </Stack>

          {isLoggedInToTwitch && (
            <>
              <DropsDivider>Play {game.data.name}</DropsDivider>
              <GameLinks
                game={game}
                platforms={gameSpecs.platforms.map(([name]) => name)}
                links={gameSpecs.platforms.map(([_, link]) => link)}
              />
            </>
          )}

          {remainingEarnableGems !== 0 && (
            <>
              <DropsDivider>COMPLETE TREEVERSE LEADERBOARD</DropsDivider>
              <Stack direction="column" gap="base.spacing.x4">
                <Body>
                  <Link rc={<NextLink href={`/games/${game.uid}`} />}>
                    Compete
                  </Link>{" "}
                  for $END, $IMX and $MON token rewards!
                </Body>
              </Stack>
            </>
          )}

          <DropsDivider>Official Streamers</DropsDivider>

          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="flex-start"
            justifyContent="flex-start"
            gap="base.spacing.x4"
          >
            {TREEVERSE_TWITCH_STREAMERS.map((streamer) => (
              <Button
                variant="secondary"
                key={streamer}
                onClick={() => {
                  window.open(`https://www.twitch.tv/${streamer}`, "_blank");
                }}
              >
                <Button.Icon icon="Twitch" />
                {streamer}
              </Button>
            ))}
          </Stack>
        </Box>
      </Stack>

      <ConfirmationDialog
        visible={isHowToDialogOpen}
        onCloseModal={onCloseHowToDialog}
      >
        <ConfirmationDialog.Content textAlign="left" sx={{ maxw: "500px" }}>
          <ConfirmationDialog.Content.CloseButtCon
            onClick={onCloseHowToDialog}
          />
          <ConfirmationDialog.Content.Title>
            How to participate
          </ConfirmationDialog.Content.Title>
          <ConfirmationDialog.Content.Caption sx={{ mt: "base.spacing.x5" }}>
            <BulletList use={<ol />}>
              <BulletList.Item size="small">
                Sign in with your Immutable Passport.
              </BulletList.Item>
              <BulletList.Item size="small">
                Log in with Twitch.
              </BulletList.Item>
              <BulletList.Item size="small">
                Go to a participating live channel (official streamers).
              </BulletList.Item>
              <BulletList.Item size="small">
                Watch {game.data.name} and claim the rewards
              </BulletList.Item>
            </BulletList>
          </ConfirmationDialog.Content.Caption>
          <ConfirmationDialog.Content.Caption sx={{ mt: "base.spacing.x3" }}>
            Claimed drops will be distributed every few days.
          </ConfirmationDialog.Content.Caption>
        </ConfirmationDialog.Content>
      </ConfirmationDialog>
    </>
  );
}

export const GameDropsLayout = dynamic(
  () => Promise.resolve(UnwrappedGameDropsLayout),
  { ssr: false },
);
